import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { createExpense, getExpense, getExpenseSubcategories, updateExpense } from '../api/expenses';
import { getStoreUsers } from '../api/stores';
import { getUsers } from '../api/users';
import { useAuth } from '../auth/AuthProvider';
import { canConfirmExpense } from '../auth/permissions';
import type { ExpenseCategory, ExpensePaymentMethod, ExpenseSubcategory, ExpenseTaxMode } from '../types/expenses';
import type { Store } from '../types/stores';
import type { User } from '../types/users';
import { formatMoney } from '../utils/costFormatting';
import { EXPENSE_CATEGORY_LABELS, EXPENSE_PAYMENT_METHOD_LABELS, EXPENSE_TAX_MODE_LABELS } from '../utils/expenseActions';
import { displayEmployeeName, mergedSubcategoryNames } from '../utils/expenseForm';
import { calculateExpenseTaxBreakdown } from '../utils/expenseTax';
import {
  GENERAL_STORE, asksStore, buildExpensePayload, emptyExpenseDraft, expenseDraftFromExpense, expenseDraftTotal, expenseStepBlocker, expenseSteps,
  hasSubcategory, nextExpenseStep, previousExpenseStep, type ExpenseDraft, type ExpenseStep,
} from '../utils/expenseWizard';
import { resolveStoreTimezone } from '../utils/timezones';
import { AddLink, Answers, ChoiceCards, DoneRow, FooterReason, Question } from './capture';
import { ActionBar } from './ui/ActionBar';
import { ProgressiveSection } from './ui/ProgressiveSection';
import { DateTimeField } from './DateTimeField';
import { ExpenseSubcategoryModal } from './ExpenseSubcategoryModal';
import { Modal } from './Modal';
import { useConfirm } from './ui/ConfirmDialog';

// Registrar gasto como asistente (FI-22, Entrega 1): una pregunta a la vez, respuestas con círculo,
// nada preseleccionado, bloques que se encogen y fecha explícita. Reemplaza al formulario anterior
// para crear Y editar (editar abre en Revisar con todo encogido). Misma API de gastos de hoy.

const STEP_TITLES: Record<ExpenseStep, string> = { STORE: 'Tienda', CATEGORY: 'Tipo de gasto', WHAT: 'Qué y a quién', AMOUNT: 'Cuánto', WHEN: 'Cuándo, cómo y factura', REVIEW: 'Revisar' };
const CATEGORY_OPTIONS = (Object.entries(EXPENSE_CATEGORY_LABELS) as Array<[ExpenseCategory, string]>).map(([value, label]) => ({ value, label }));
const PAYMENT_OPTIONS = (Object.entries(EXPENSE_PAYMENT_METHOD_LABELS) as Array<[ExpensePaymentMethod, string]>).map(([value, label]) => ({ value, label }));
const TAX_HINTS: Record<ExpenseTaxMode, string> = { NONE: 'El monto es el total, sin impuesto', INCLUDED: 'El monto ya trae el IVA', CUSTOM: 'El monto es el subtotal; se suma un porcentaje' };
const TAX_OPTIONS = (Object.entries(EXPENSE_TAX_MODE_LABELS) as Array<[ExpenseTaxMode, string]>).map(([value, label]) => ({ value, label, hint: TAX_HINTS[value] }));
const INVOICE_OPTIONS = [{ value: 'yes' as const, label: 'Sí', hint: 'Con folio de factura o nota' }, { value: 'no' as const, label: 'No', hint: 'Sin comprobante fiscal' }];

type WhenBlock = 'date' | 'payment' | 'invoiced';
const NOTHING: Record<WhenBlock, boolean> = { date: false, payment: false, invoiced: false };

function formatWhen(iso: string, timeZone: string) {
  const text = new Date(iso).toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', timeZone });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function ExpenseCaptureModal({ expenseId, stores, onClose, onSaved }: { expenseId: string | undefined; stores: Store[]; onClose: () => void; onSaved: (message: string) => Promise<void> | void }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isEmployee = user?.role === 'EMPLOYEE';
  const isEditing = expenseId !== undefined;
  const askStore = asksStore(stores.length, isEmployee);
  const steps = expenseSteps(askStore);
  const [draft, setDraft] = useState<ExpenseDraft>(() => emptyExpenseDraft({ storeIds: stores.map((store) => store.id), isEmployee }));
  const [step, setStep] = useState<ExpenseStep>(steps[0]!);
  const [dateReady, setDateReady] = useState(false);
  const [expanded, setExpanded] = useState<Record<WhenBlock, boolean>>(NOTHING);
  const [loadedDate, setLoadedDate] = useState<string>();
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [employees, setEmployees] = useState<Array<Pick<User, 'id' | 'fullName' | 'username' | 'isActive'>>>();
  const [subcategories, setSubcategories] = useState<ExpenseSubcategory[]>();
  const [subcategoryModalOpen, setSubcategoryModalOpen] = useState(false);

  const patch = (changes: Partial<ExpenseDraft>) => setDraft((current) => ({ ...current, ...changes }));

  // La tienda única llega después (stores es asíncrono): si no es una elección, se resuelve sola.
  useEffect(() => {
    if (isEditing || askStore) return;
    const only = stores[0]?.id ?? GENERAL_STORE;
    setDraft((current) => (current.storeChoice ? current : { ...current, storeChoice: only }));
  }, [stores, askStore, isEditing]);

  // Editar: el gasto guardado se abre en Revisar con todo encogido.
  useEffect(() => {
    if (!expenseId) return;
    setIsLoading(true);
    void getExpense(expenseId).then((result) => {
      setDraft(expenseDraftFromExpense(result.expense));
      setLoadedDate(result.expense.expenseDate);
      setDateReady(true);
      setStep('REVIEW');
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar el gasto.'))).finally(() => setIsLoading(false));
  }, [expenseId]);

  // Sueldos: el empleado se elige de una lista real (la de su tienda para un empleado; todos para
  // dueño/gerente), cargada solo cuando hace falta.
  useEffect(() => {
    if (draft.category !== 'PAYROLL' || employees !== undefined) return;
    const storeId = draft.storeChoice && draft.storeChoice !== GENERAL_STORE ? draft.storeChoice : '';
    if (isEmployee) {
      if (!storeId) { setEmployees([]); return; }
      void getStoreUsers(storeId).then((result) => setEmployees(result.users.filter((employee) => employee.isActive))).catch(() => setEmployees([]));
      return;
    }
    void getUsers().then((result) => setEmployees(result.users.filter((employee) => employee.isActive))).catch(() => setEmployees([]));
  }, [draft.category, draft.storeChoice, employees, isEmployee]);

  const loadSubcategories = useCallback((category: ExpenseCategory) => {
    setSubcategories(undefined);
    void getExpenseSubcategories(category).then((result) => setSubcategories(result.items)).catch(() => setSubcategories([]));
  }, []);
  useEffect(() => { if (hasSubcategory(draft.category)) loadSubcategories(draft.category as ExpenseCategory); }, [draft.category, loadSubcategories]);

  const blocker = expenseStepBlocker(step, draft, { dateReady, steps });
  const timeZone = resolveStoreTimezone(stores, { storeId: draft.storeChoice && draft.storeChoice !== GENERAL_STORE ? draft.storeChoice : undefined });
  const storeLabel = draft.storeChoice === GENERAL_STORE ? 'Gasto general (sin tienda)' : stores.find((store) => store.id === draft.storeChoice)?.name ?? '';
  const total = expenseDraftTotal(draft);
  const hasContent = Boolean(draft.category || draft.concept.trim() || draft.amount);

  const save = async (confirmNow: boolean) => {
    if (isSaving || blocker) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const payload = buildExpensePayload(draft);
      if (isEditing) {
        await updateExpense(expenseId, payload);
        await onSaved('Gasto actualizado.');
      } else {
        await createExpense({ ...payload, confirm: confirmNow });
        await onSaved(confirmNow ? 'Gasto guardado y confirmado.' : 'Gasto guardado como borrador.');
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el gasto.'));
    } finally {
      setIsSaving(false);
    }
  };
  const cancel = async () => {
    if (isSaving) return;
    if (!isEditing && hasContent && !(await confirm({ title: '¿Descartar el gasto?', description: 'Lo capturado hasta ahora se perderá.', confirmLabel: 'Descartar', cancelLabel: 'Seguir capturando', danger: true }))) return;
    onClose();
  };
  const goNext = () => {
    if (blocker) return;
    if (step === 'REVIEW') { void save(true); return; }
    setExpanded(NOTHING);
    setStep(nextExpenseStep(step, steps));
  };
  const goTo = (target: ExpenseStep) => { setExpanded(NOTHING); setStep(target); };

  // ── Bloques ────────────────────────────────────────────────────────────────────────────────────
  const categoryLabel = draft.category ? EXPENSE_CATEGORY_LABELS[draft.category] : '';
  const whatLabel = `${draft.concept.trim()}${draft.vendorName.trim() ? ` · ${draft.vendorName.trim()}` : ''}`;
  const amountLabel = total !== null && draft.taxMode ? `${formatMoney(String(total))} · ${EXPENSE_TAX_MODE_LABELS[draft.taxMode]}` : '';
  const invoicedLabel = draft.invoiced === 'yes' ? `Sí${draft.reference.trim() ? ` · folio ${draft.reference.trim()}` : ''}` : draft.invoiced === 'no' ? 'No' : '';

  // P-079: los tres bloques del paso «Cuándo, cómo y factura» con la pieza común (`ProgressiveSection`): la misma
  // condición de completo, el mismo `expanded`; «Cambiar» reabre con el valor puesto.
  const dateBlock = (
    <ProgressiveSection busy={isSaving} complete={Boolean(dateReady && draft.dateIso)} label="Fecha y hora" onOpen={() => setExpanded({ ...NOTHING, date: true })} open={expanded.date} summary={draft.dateIso ? formatWhen(draft.dateIso, timeZone) : ''}>
      <Question>¿Cuándo fue?</Question>
      <Answers>
        <DateTimeField
          explicit
          label=""
          startAcknowledged={dateReady}
          value={loadedDate ?? (draft.dateIso || null)}
          onChange={(iso, ready) => { const changed = iso !== draft.dateIso; patch({ dateIso: iso }); setDateReady(ready); if (ready && changed) setExpanded((current) => ({ ...current, date: false })); }}
        />
      </Answers>
    </ProgressiveSection>
  );
  const paymentBlock = (
    <ProgressiveSection busy={isSaving} complete={Boolean(draft.paymentMethod)} label="Cómo pagaste" onOpen={() => setExpanded({ ...NOTHING, payment: true })} open={expanded.payment} summary={draft.paymentMethod ? EXPENSE_PAYMENT_METHOD_LABELS[draft.paymentMethod] : ''}>
      <Question>¿Cómo pagaste?</Question>
      <ChoiceCards ariaLabel="Método de pago" onChange={(value) => { patch({ paymentMethod: value }); setExpanded(NOTHING); }} options={PAYMENT_OPTIONS} value={draft.paymentMethod} />
    </ProgressiveSection>
  );
  const invoicedBlock = (
    <ProgressiveSection busy={isSaving} complete={Boolean(draft.invoiced)} label="¿Te dieron factura?" onOpen={() => setExpanded({ ...NOTHING, invoiced: true })} open={expanded.invoiced} summary={invoicedLabel}>
      <Question>¿Te dieron factura?</Question>
      <ChoiceCards ariaLabel="Factura" onChange={(value) => { patch({ invoiced: value }); setExpanded(value === 'yes' ? { ...NOTHING, invoiced: true } : NOTHING); }} options={INVOICE_OPTIONS} value={draft.invoiced} />
      {draft.invoiced === 'yes' && <Answers><label className="field"><span>Folio de la factura o nota</span><input maxLength={200} onChange={(event) => patch({ reference: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); setExpanded(NOTHING); } }} placeholder="Opcional" value={draft.reference} /></label></Answers>}
    </ProgressiveSection>
  );

  let body: JSX.Element;
  if (isLoading) {
    body = <div className="inline-loading"><span className="spinner" />Cargando...</div>;
  } else {
    switch (step) {
      case 'STORE':
        body = <>
          <Question>¿En qué tienda fue el gasto?</Question>
          <ChoiceCards ariaLabel="Tienda" onChange={(value) => patch({ storeChoice: value })} options={[...stores.map((store) => ({ value: store.id, label: store.name })), { value: GENERAL_STORE, label: 'Gasto general', hint: 'No es de una tienda en particular' }]} stack value={draft.storeChoice} />
        </>;
        break;
      case 'CATEGORY':
        body = <>
          <Question>¿De qué tipo es el gasto?</Question>
          <ChoiceCards ariaLabel="Tipo de gasto" onChange={(value) => { patch({ category: value, subcategory: '', vendorName: value === 'PAYROLL' || draft.category === 'PAYROLL' ? '' : draft.vendorName }); setEmployees(undefined); }} options={CATEGORY_OPTIONS} value={draft.category} variant="chips" />
          {hasSubcategory(draft.category) && <>
            <Question>¿Qué tipo de {categoryLabel.toLowerCase()}?</Question>
            {subcategories === undefined
              ? <Answers><p className="wiz-hint">Cargando…</p></Answers>
              : <ChoiceCards ariaLabel="Subcategoría" onChange={(value) => patch({ subcategory: value })} options={mergedSubcategoryNames(draft.category as ExpenseCategory, subcategories).map((name) => ({ value: name, label: name }))} value={draft.subcategory} variant="chips" />}
            <AddLink onClick={() => setSubcategoryModalOpen(true)}>+ Agregar subcategoría</AddLink>
          </>}
        </>;
        break;
      case 'WHAT':
        body = <>
          <Question>¿Qué pagaste?</Question>
          <Answers><label className="field"><span>Concepto</span><input autoFocus maxLength={200} onChange={(event) => patch({ concept: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="Ej. Renta de septiembre" value={draft.concept} /></label></Answers>
          {draft.category === 'PAYROLL'
            ? <>
              <Question>¿A qué empleado?</Question>
              {employees === undefined
                ? <Answers><p className="wiz-hint">Cargando empleados…</p></Answers>
                : employees.length === 0
                  ? <Answers><p className="wiz-hint">No hay empleados activos para elegir.</p></Answers>
                  : <ChoiceCards ariaLabel="Empleado" onChange={(value) => patch({ vendorName: value })} options={[...(draft.vendorName && !employees.some((employee) => displayEmployeeName(employee) === draft.vendorName) ? [{ value: draft.vendorName, label: draft.vendorName, hint: 'Ya no está en la lista' }] : []), ...employees.map((employee) => ({ value: displayEmployeeName(employee), label: displayEmployeeName(employee) }))]} stack value={draft.vendorName} />}
            </>
            : <>
              <Question>¿A quién se le pagó?</Question>
              <Answers><label className="field"><span>Proveedor (opcional)</span><input maxLength={200} onChange={(event) => patch({ vendorName: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="Ej. Inmobiliaria del Centro" value={draft.vendorName} /></label></Answers>
            </>}
        </>;
        break;
      case 'AMOUNT': {
        const breakdown = Number(draft.amount) > 0 && draft.taxMode ? calculateExpenseTaxBreakdown(Number(draft.amount), draft.taxMode, draft.taxPercentage === '' ? null : Number(draft.taxPercentage)) : null;
        body = <>
          <Question>¿Cuánto pagaste?</Question>
          <Answers><label className="field wiz-big-input"><span>Monto (MXN){draft.taxMode === 'CUSTOM' ? ' · subtotal sin IVA' : ''}</span><input autoFocus enterKeyHint="done" inputMode="decimal" min="0.01" onChange={(event) => patch({ amount: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="0.00" step="any" type="number" value={draft.amount} /></label></Answers>
          <Question>¿Cómo va el IVA?</Question>
          <ChoiceCards ariaLabel="IVA" onChange={(value) => patch({ taxMode: value, taxPercentage: value === 'CUSTOM' ? draft.taxPercentage : '' })} options={TAX_OPTIONS} value={draft.taxMode} />
          {draft.taxMode === 'CUSTOM' && <Answers><label className="field"><span>Porcentaje de IVA</span><input enterKeyHint="done" inputMode="decimal" max="100" min="0" onChange={(event) => patch({ taxPercentage: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="16" step="any" type="number" value={draft.taxPercentage} /></label></Answers>}
          {breakdown && <div aria-label="Desglose de IVA" className="wiz-breakdown"><article><span>Subtotal</span><strong>{formatMoney(breakdown.subtotalAmount.toFixed(2))}</strong></article><article><span>IVA ({(breakdown.taxPercentage ?? 0).toFixed(1)}%)</span><strong>{formatMoney(breakdown.taxAmount.toFixed(2))}</strong></article><article><span>Total</span><strong>{formatMoney(breakdown.totalAmount.toFixed(2))}</strong></article></div>}
        </>;
        break;
      }
      case 'WHEN':
        body = <>{dateBlock}{paymentBlock}{invoicedBlock}</>;
        break;
      case 'REVIEW':
      default:
        body = <>
          <Question>{isEditing ? 'Revisa los cambios del gasto' : 'Revisa el gasto antes de guardar'}</Question>
          {askStore && <DoneRow label="Tienda" onChange={() => goTo('STORE')} value={storeLabel} />}
          <DoneRow label="Tipo" onChange={() => goTo('CATEGORY')} value={`${categoryLabel}${draft.subcategory ? ` · ${draft.subcategory}` : ''}` || 'Sin elegir'} />
          <DoneRow label="Qué y a quién" onChange={() => goTo('WHAT')} value={whatLabel || 'Sin capturar'} />
          <DoneRow label="Cuánto" onChange={() => goTo('AMOUNT')} value={amountLabel || 'Sin capturar'} />
          <DoneRow label="Cuándo" onChange={() => { goTo('WHEN'); setExpanded({ ...NOTHING, date: true }); }} value={draft.dateIso ? formatWhen(draft.dateIso, timeZone) : 'Sin elegir'} />
          <DoneRow label="Cómo pagaste" onChange={() => { goTo('WHEN'); setExpanded({ ...NOTHING, payment: true }); }} value={draft.paymentMethod ? EXPENSE_PAYMENT_METHOD_LABELS[draft.paymentMethod] : 'Sin elegir'} />
          <DoneRow label="Factura" onChange={() => { goTo('WHEN'); setExpanded({ ...NOTHING, invoiced: true }); }} value={invoicedLabel || 'Sin responder'} />
          <Answers><label className="field"><span>Notas (opcional)</span><textarea onChange={(event) => patch({ notes: event.target.value })} placeholder="Opcional" rows={2} value={draft.notes} /></label></Answers>
        </>;
        break;
    }
  }

  const isReview = step === 'REVIEW';
  const primaryLabel = isReview ? (isSaving ? 'Guardando...' : isEditing ? 'Guardar cambios' : canConfirmExpense(user?.role) ? 'Guardar y confirmar' : 'Guardar') : 'Siguiente';
  const onPrimary = () => { if (isReview) void save(!isEditing && canConfirmExpense(user?.role)); else goNext(); };

  return <>
    <Modal
      title={isEditing ? 'Editar gasto' : 'Registrar gasto'}
      description={`Paso ${steps.indexOf(step) + 1} de ${steps.length} · ${STEP_TITLES[step]}`}
      isBusy={isSaving}
      onClose={() => void cancel()}
      // P-078 (ActionBar v2 · H): el mismo pie (nota, Cancelar · Guardar borrador · Atrás y la primaria), con la primaria a
      // todo el ancho en el teléfono (antes caía sola a media anchura). Ninguna condición cambia.
      footer={<ActionBar aria-label="Acciones del gasto" note={blocker ? <FooterReason>{blocker}</FooterReason> : undefined} primary={<button className="button button--primary" disabled={isSaving || isLoading || Boolean(blocker)} onClick={onPrimary} type="button">{primaryLabel}</button>}>
        <button className="text-button" disabled={isSaving} onClick={() => void cancel()} type="button">Cancelar</button>
        {!isEditing && <button className="text-button" disabled={isSaving || Boolean(blocker) || !isReview} onClick={() => void save(false)} title={isReview ? undefined : 'Disponible al revisar'} type="button">Guardar borrador</button>}
        {step !== steps[0] && <button className="button button--ghost" disabled={isSaving} onClick={() => goTo(previousExpenseStep(step, steps))} type="button">Atrás</button>}
      </ActionBar>}
    >
      {error && <div className="alert alert--error">{error}</div>}
      {body}
    </Modal>
    {subcategoryModalOpen && draft.category && (
      <ExpenseSubcategoryModal
        category={draft.category}
        onClose={() => setSubcategoryModalOpen(false)}
        onCreated={(name) => { patch({ subcategory: name }); setSubcategoryModalOpen(false); loadSubcategories(draft.category as ExpenseCategory); }}
      />
    )}
  </>;
}
